/**
 * «Рассылки»: вкладки (`?tab=`); пока одна — «Wazzup» (WABA): шаблоны WhatsApp. Внутри —
 * «Номера» (за 24/48/72 ч или один день `?day=`) и «По дням» (`?view=days`,
 * период `from`/`to`, `group=week` — по неделям; клик по числу — номера справа).
 * Воронка без пересечений: «записались», а остальные — где потеряли (не дошло ·
 * не прочитали · прочитали и молчат · пообщались · «Актуально» · «Не актуально»);
 * сумма = отправлено. По каждому номеру — текущий лид, а если есть сделка — её
 * стадия. Спека `docs/specs/2026-09-25-mailing-waba-design.md`; числа — как у
 * `prokat-runner mailing-waba`. Комментарии сотрудника к номеру — прямо в таблице.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, X } from "lucide-react";
import type { ReactNode } from "react";
import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { LeadLink } from "@/components/LeadLink";
import { isValidDate, iso, plusDays } from "@/lib/dates";
import { shortTime } from "@/lib/leads";
import {
  addWabaComment,
  loadWaba,
  loadWabaChat,
  loadWabaDays,
  type Waba,
  type WabaComment,
  type WabaDay,
  type WabaGroup,
  type WabaMessage,
  type WabaResult,
  type WabaRow,
  type WabaStep,
} from "@/lib/mailing";
import { FittingTab } from "@/pages/MailingFitting";
import { RemindTab } from "@/pages/MailingRemind";
import { LinksTab } from "@/pages/MailingLinks";
import { TemplatesTab } from "@/pages/MailingTemplates";

const HOURS = [24, 48, 72, 168] as const;
const DEFAULT_HOURS = 24;


type Filter = "all" | WabaResult;

const LOST: { id: WabaStep; label: string; hint: string; tone: string; key?: boolean }[] = [
  { id: "failed", label: "Не дошло", hint: "красная галочка: номера нет в WhatsApp, лимит маркетинга, эксперимент Meta", tone: "text-err" },
  { id: "unread", label: "Не прочитали", hint: "одна или две серые галочки, ответа нет, не записались", tone: "text-warn" },
  { id: "read", label: "Прочитали, молчат", hint: "прочитали, не ответили и не записались", tone: "text-warn" },
  { id: "talk", label: "Пообщались, не записались", hint: "клиент написал текстом (без кнопки), но записи нет", tone: "text-warn" },
  {
    id: "yes",
    label: "«Актуально», не записались",
    hint: "нажали «Актуально», а сделки нет или она не дошла до «Ждем клиента» — главные потерянные",
    tone: "text-err",
    key: true,
  },
  { id: "no", label: "«Не актуально»", hint: "нажали «Не актуально» и не записались", tone: "text-fg-muted" },
];

/** Что человек сделал с сообщением — подпись шага. */
const STEP: Record<WabaStep, { mark: string; label: string; tone: string }> = {
  failed: { mark: "✕", label: "не дошло", tone: "text-err" },
  unread: { mark: "✓", label: "не прочитали", tone: "text-warn" },
  read: { mark: "✓✓", label: "прочитали, молчат", tone: "text-accent" },
  talk: { mark: "✎", label: "пообщались", tone: "text-fg" },
  yes: { mark: "●", label: "«Актуально»", tone: "text-ok" },
  no: { mark: "○", label: "«Не актуально»", tone: "text-fg-muted" },
};

function match(r: WabaRow, f: Filter): boolean {
  return f === "all" || r.result === f;
}

/** Галочки нашего сообщения — как в WhatsApp. */
const TICK: Record<string, { mark: string; tone: string; label: string }> = {
  sent: { mark: "✓", tone: "text-fg-faint", label: "отправлено" },
  delivered: { mark: "✓✓", tone: "text-fg-faint", label: "доставлено" },
  read: { mark: "✓✓", tone: "text-accent", label: "прочитано" },
  error: { mark: "✕", tone: "text-err", label: "не доставлено" },
};

/** Отчерк дня в переписке: «29 сентября, пн». */
function dayTitle(date: string): string {
  const d = new Date(`${date.slice(0, 10)}T12:00:00`);
  const wd = d.toLocaleDateString("ru-RU", { weekday: "short" });
  return `${d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}, ${wd}`;
}

function Message({ m, sent }: { m: WabaMessage; sent: string }) {
  const tick = m.out && m.status ? TICK[m.status] : undefined;
  // отправка этой строки обведена — видно, что было до и после
  const self = m.template !== null && m.at?.slice(0, 16) === sent.slice(0, 16);
  return (
    <div className={`flex ${m.out ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-[var(--radius-card)] border border-border px-2.5 py-1.5 ${
          m.out ? "bg-bg-subtle" : "bg-bg"
        } ${self ? "ring-1 ring-accent" : ""}`}
      >
        <div className="mb-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-fg-faint">
          <span>{m.out ? (m.template ? `шаблон «${m.template}»` : m.author || "мы") : "клиент"}</span>
          {m.button && <span className="font-medium text-accent">кнопка</span>}
          {m.type !== "text" && m.type !== "wapi_template" && <span>{m.type}</span>}
        </div>
        {m.text ? (
          <div className={`whitespace-pre-wrap break-words ${m.button ? "font-medium" : ""}`}>{m.text}</div>
        ) : (
          !m.uri && <div className="text-fg-faint">(пусто)</div>
        )}
        {m.uri && (
          <a href={m.uri} target="_blank" rel="noreferrer noopener" className="text-accent underline">
            {m.type === "image" ? "картинка" : "файл"}
          </a>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[11px] tabular-nums text-fg-faint">
          {m.at?.slice(11, 16)}
          {tick && (
            <span className={`font-semibold ${tick.tone}`} title={m.error || tick.label}>
              {tick.mark}
            </span>
          )}
        </div>
        {m.error && <div className="text-[11px] text-err">{m.error}</div>}
      </div>
    </div>
  );
}

/** Вся переписка номера из своей копии (вебхук Wazzup + выгрузка истории). */
function Dialog({ phone, sent }: { phone: string; sent: string }) {
  const q = useQuery({ queryKey: ["mailing-waba-chat", phone], queryFn: () => loadWabaChat(phone) });
  if (q.isPending) return <span className="text-fg-muted">Загружаю переписку…</span>;
  if (q.isError) return <span className="text-err">Не удалось получить переписку: {String(q.error.message)}</span>;
  const msgs = q.data.messages;
  if (msgs.length === 0) return <span className="text-fg-muted">Сообщений по номеру нет.</span>;
  return (
    <div className="flex max-w-[760px] flex-col gap-1.5">
      {msgs.map((m, i) => (
        <Fragment key={m.guid}>
          {m.at && m.at.slice(0, 10) !== msgs[i - 1]?.at?.slice(0, 10) && (
            <div className={`flex items-center gap-2 text-[11.5px] font-medium text-fg-faint ${i ? "mt-2" : ""}`}>
              {dayTitle(m.at)}
              <span className="h-px flex-1 bg-border" />
            </div>
          )}
          <Message m={m} sent={sent} />
        </Fragment>
      ))}
    </div>
  );
}

/** Комментарии сотрудника к номеру: читаются и пишутся прямо в строке. */
function Comments({ r, onSaved }: { r: WabaRow; onSaved: (phone: string, list: WabaComment[]) => void }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const save = useMutation({
    mutationFn: () => addWabaComment(r.phone, draft.trim()),
    onSuccess: (list) => {
      onSaved(r.phone, list);
      setDraft("");
      setEdit(false);
    },
  });
  const submit = () => {
    if (draft.trim() && !save.isPending) save.mutate();
  };
  return (
    <div className="flex w-[260px] flex-col gap-1">
      {r.comments.map((c) => (
        <div key={c.id} className="rounded-[var(--radius-ui)] bg-accent-dim px-2 py-1">
          <div className="whitespace-pre-wrap break-words">{c.text}</div>
          <div className="text-[11px] text-fg-faint">
            {c.user} · {shortTime(c.at)}
          </div>
        </div>
      ))}
      {edit ? (
        <div className="flex flex-col gap-1">
          <textarea
            autoFocus
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              } else if (e.key === "Escape") setEdit(false);
            }}
            placeholder="что сделали с номером… Enter — сохранить"
            className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1 text-[12.5px] outline-none focus:border-accent"
          />
          <div className="flex items-center gap-2 text-[12px]">
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || save.isPending}
              className="h-6 rounded-[var(--radius-ui)] border border-accent px-2 text-accent-text disabled:opacity-50"
            >
              {save.isPending ? "Сохраняю…" : "Сохранить"}
            </button>
            <button type="button" onClick={() => setEdit(false)} className="text-fg-muted hover:text-fg">
              отмена
            </button>
            {save.isError && <span className="text-err">{String(save.error.message)}</span>}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEdit(true)}
          className="self-start text-[12px] text-fg-faint hover:text-accent-text"
          data-annot={`добавить комментарий ${r.phone}`}
        >
          + комментарий
        </button>
      )}
    </div>
  );
}

const COLUMNS = 9;

/** Копировать номер в буфер; галочка на секунду — скопировано. */
function CopyPhone({ phone }: { phone: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-comments
      onClick={() =>
        navigator.clipboard.writeText(phone).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1000);
        })
      }
      className="ml-1.5 inline-flex align-middle text-fg-faint hover:text-accent-text"
      title="скопировать номер"
      aria-label={`скопировать номер ${phone}`}
    >
      {done ? <Check size={12} className="text-ok" /> : <Copy size={12} />}
    </button>
  );
}

function Row({ r, onComments }: { r: WabaRow; onComments: (phone: string, list: WabaComment[]) => void }) {
  const [open, setOpen] = useState(false);
  const td = "px-3 py-1.5 align-top";
  const res = STEP[r.step];
  return (
    <>
    <tr
      className={`cursor-pointer border-t border-border hover:bg-bg-hover ${open ? "bg-bg-hover" : ""}`}
      onClick={(e) => {
        // ссылки строки (чат Wazzup, лид, сделка) и комментарии работают сами, переписку не раскрывают
        if (!(e.target as HTMLElement).closest("a, [data-comments]")) setOpen(!open);
      }}
      title={open ? "Свернуть переписку" : "Показать переписку"}
      data-annot={`отправка ${r.phone}`}
    >
      <td className={`${td} whitespace-nowrap tabular-nums text-fg-muted`}>
        <span className="mr-1 inline-block w-2 text-fg-faint">{open ? "▾" : "▸"}</span>
        {shortTime(r.sent)}
      </td>
      <td className={`${td} whitespace-nowrap font-mono tabular-nums`}>
        {r.chat_url ? (
          <a href={r.chat_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 underline" title="чат в Wazzup">
            {r.phone} <ExternalLink size={11} aria-hidden />
          </a>
        ) : (
          r.phone
        )}
        <CopyPhone phone={r.phone} />
      </td>
      <td className={`${td} cursor-auto`} data-comments title="" data-annot={`комментарии ${r.phone}`}>
        <Comments r={r} onSaved={onComments} />
      </td>
      <td className={`${td} whitespace-nowrap`}>{r.template}</td>
      <td className={td}>
        {r.result === "booked" && <div className="whitespace-nowrap font-medium text-ok">✓ записались</div>}
        <span className={`whitespace-nowrap ${r.result === "booked" ? "text-[11.5px] text-fg-faint" : res.tone}`} title={r.error || undefined}>
          <span aria-hidden="true" className="font-semibold">
            {res.mark}
          </span>{" "}
          {r.result === "booked" ? `до записи: ${res.label}` : res.label}
        </span>
        {r.error && <div className="max-w-[200px] truncate text-[11.5px] text-fg-faint">{r.error}</div>}
      </td>
      <td className={td}>
        {r.talk ? (
          <div className="flex max-w-[280px] flex-col" title={r.first_text ?? undefined}>
            <span className="truncate">«{r.first_text}»</span>
            <span className="text-[11.5px] text-fg-faint tabular-nums">
              {shortTime(r.replied)}
              {r.talk_count > 1 && ` · сообщений ${r.talk_count}`}
            </span>
          </div>
        ) : (
          <span className="text-fg-faint">—</span>
        )}
      </td>
      <td className={td}>
        {r.lead ? (
          <div className="flex flex-col whitespace-nowrap">
            <LeadLink id={r.lead.id} />
            <span className="text-[11.5px] text-fg-muted">
              {r.lead.name}
              {r.leads > 1 && <span className="text-fg-faint"> · лидов {r.leads}</span>}
            </span>
          </div>
        ) : (
          <span className="text-fg-faint">лида нет</span>
        )}
      </td>
      <td className={td}>
        {r.deal ? (
          <div className="flex flex-col whitespace-nowrap">
            <Link to={`/deals/${r.deal.id}`} className="font-mono underline" title="карточка сделки">
              {r.deal.id}
            </Link>
            <span className="text-[11.5px] text-fg-muted">{r.deal.name}</span>
          </div>
        ) : (
          <span className="text-fg-faint">—</span>
        )}
      </td>
      <td className={`${td} whitespace-nowrap tabular-nums`}>
        {r.booked ? <span className="text-ok">✓ {shortTime(r.booked)}</span> : <span className="text-fg-faint">—</span>}
        {r.came && <div className="text-[11.5px] text-fg-muted">пришли {shortTime(r.came)}</div>}
        {r.bron && <div className="text-[11.5px] text-ok">бронь {shortTime(r.bron)}</div>}
      </td>
    </tr>
    {open && (
      <tr>
        <td colSpan={COLUMNS} className="bg-bg px-8 pb-3 pt-2 text-[12.5px] leading-[1.45]" data-annot={`переписка ${r.phone}`}>
          <Dialog phone={r.phone} sent={r.sent} />
        </td>
      </tr>
    )}
    </>
  );
}

function Tile({
  label,
  n,
  tone,
  hint,
  active,
  onClick,
  accent,
  children,
}: {
  label: string;
  n: number;
  tone: string;
  hint: string;
  active: boolean;
  onClick: () => void;
  accent?: boolean | undefined;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hint}
      className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border p-3 text-left ${
        active ? "border-accent bg-bg-hover" : accent && n ? "border-err bg-bg-subtle hover:bg-bg-hover" : "border-border bg-bg-subtle hover:bg-bg-hover"
      }`}
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[22px] font-semibold leading-none tabular-nums ${n ? tone : "text-fg-faint"}`}>{n}</div>
      {children}
    </button>
  );
}

function RowsTable({ rows, onComments }: { rows: WabaRow[]; onComments: (phone: string, list: WabaComment[]) => void }) {
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  if (rows.length === 0) return <div className="text-[12.5px] text-fg-muted">Номеров нет.</div>;
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
      <table className="w-full text-[12.5px]">
        <thead className="text-left text-[11.5px] text-fg-faint">
          <tr>
            <th className={th}>Отправлено</th>
            <th className={th}>Телефон</th>
            <th className={th} title="что сделали с номером — пишет сотрудник, видно всем">
              Комментарий
            </th>
            <th className={th}>Шаблон</th>
            <th className={th} title="записались — или шаг, на котором потеряли">
              Итог
            </th>
            <th className={th} title="первое сообщение клиента текстом после рассылки">
              Написал
            </th>
            <th className={th} title="последний лид номера и его статус сейчас">
              Лид сейчас
            </th>
            <th className={th} title="последняя сделка лидов номера и её стадия сейчас">
              Сделка сейчас
            </th>
            <th className={th} title="когда после сообщения сделка перешла в «Ждем клиента»; ниже — приход в салон и договор брони">
              Записали
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row key={r.guid} r={r} onComments={onComments} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Body({ w, onComments }: { w: Waba; onComments: (phone: string, list: WabaComment[]) => void }) {
  const [filter, setFilter] = useState<Filter>("all");
  const toggle = (f: Filter) => setFilter(filter === f ? "all" : f);
  const [template, setTemplate] = useState<string>("");
  const scoped = template ? w.rows.filter((r) => r.template === template) : w.rows;
  const count = (f: Filter) => scoped.filter((r) => match(r, f)).length;
  const rows = scoped.filter((r) => match(r, filter));
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
        <span>
          {w.day ? dayTitle(w.day) : `${shortTime(w.since)} – ${shortTime(w.until)}`}; шаблон:
        </span>
        <div className="flex gap-1" role="group" aria-label="Шаблон">
          {["", ...Object.keys(w.templates)].map((t) => (
            <button
              key={t || "all"}
              type="button"
              onClick={() => setTemplate(t)}
              aria-pressed={template === t}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 ${
                template === t ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {t ? `${t} · ${w.templates[t]}` : `все · ${w.total}`}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr]" data-annot="плитки WABA">
        <div className="grid grid-cols-2 gap-2">
          <Tile active={filter === "all"} onClick={() => setFilter("all")} label="Отправлено" n={scoped.length} tone="text-fg" hint="шаблоны WhatsApp (WABA) за период" />
          <Tile
            active={filter === "booked"}
            onClick={() => toggle("booked")}
            label="Записались"
            n={count("booked")}
            tone="text-ok"
            hint="после сообщения сделка номера перешла в «Ждем клиента» (или создана в ней) — любым путём, звонком КЦ тоже"
          >
            {count("booked") > 0 && (
              <div className="text-[11.5px] leading-snug text-fg-faint">
                до записи:{" "}
                {(Object.keys(STEP) as WabaStep[])
                  .map((k) => [k, scoped.filter((r) => r.result === "booked" && r.step === k).length] as const)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${STEP[k].label} ${n}`)
                  .join(" · ")}
              </div>
            )}
          </Tile>
        </div>
        <div className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border p-2" data-annot="где потеряли">
          <div className="px-1 text-[12px] font-medium text-fg-muted">
            Не записались — где потеряли: {scoped.length - count("booked")}
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {LOST.map((t) => (
              <Tile
                key={t.id}
                active={filter === t.id}
                onClick={() => toggle(t.id)}
                label={t.label}
                n={count(t.id)}
                tone={t.tone}
                hint={t.hint}
                accent={t.key}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-[12px] text-fg-faint">
        Каждое сообщение — ровно в одной плитке: сумма «Записались» и «где потеряли» = «Отправлено». Лид и сделка — текущее
        состояние в CRM. Нажмите на строку — под ней вся переписка номера в WhatsApp. Комментарий пишется к номеру и виден
        всем, в каждой его отправке.
      </div>
      <RowsTable rows={rows} onComments={onComments} />
    </>
  );
}

/** URL-параметры вкладки: `view`, `hours`, `day`, `days`; пустое значение — убрать. */
function useWabaParams() {
  const [params, setParams] = useSearchParams();
  const patch = (next: Record<string, string | null>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) out.delete(k);
      else out.set(k, v);
    }
    setParams(out, { replace: true });
  };
  return { params, patch };
}

function Segmented<T extends string | number>({
  items,
  value,
  onChange,
  label,
}: {
  items: readonly { id: T; label: string }[];
  value: T | null;
  onChange: (id: T) => void;
  label: string;
}) {
  return (
    <div className="flex gap-1" role="group" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          aria-pressed={value === it.id}
          className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
            value === it.id ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/** «Номера»: каждая отправка строкой — за последние часы или за один день. */
function ListView() {
  const { params, patch } = useWabaParams();
  const raw = Number(params.get("hours"));
  const hours = (HOURS as readonly number[]).includes(raw) ? raw : DEFAULT_HOURS;
  const day = /^\d{4}-\d{2}-\d{2}$/.test(params.get("day") ?? "") ? params.get("day") : null;
  const key = ["mailing-waba", hours, day] as const;
  const q = useQuery({ queryKey: key, queryFn: () => loadWaba(hours, day) });
  const client = useQueryClient();
  // сохранённый комментарий кладём во все отправки номера, не пересчитывая всю рассылку
  const onComments = (phone: string, list: WabaComment[]) =>
    client.setQueryData<Waba>(key, (w) => w && { ...w, rows: w.rows.map((r) => (r.phone === phone ? { ...r, comments: list } : r)) });
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12px] text-fg-faint">за последние</span>
        <Segmented
          label="Период"
          items={HOURS.map((h) => ({ id: h, label: h === 168 ? "7 дн" : `${h} ч` }))}
          value={day ? null : hours}
          onChange={(h) => patch({ hours: h === DEFAULT_HOURS ? null : String(h), day: null })}
        />
        {day && (
          <span className="inline-flex h-7 items-center gap-2 rounded-[var(--radius-ui)] border border-accent px-2.5 text-[12.5px]">
            день {dayTitle(day)}
            <button type="button" onClick={() => patch({ day: null })} className="text-fg-muted hover:text-fg" title="вернуться к последним часам">
              ✕
            </button>
          </span>
        )}
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по Wazzup и CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить рассылки: {String(q.error.message)}</div>
      ) : (
        <Body key={day ?? hours} w={q.data} onComments={onComments} />
      )}
    </>
  );
}

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

function pct(n: number, of: number): string {
  return of ? `${Math.round((n / of) * 100)}%` : "";
}

/** Полоса дня: записались и места потерь в долях, длина — от самого загруженного дня. */
const BAR: { id: WabaResult; cls: string }[] = [
  { id: "booked", cls: "bg-ok" },
  { id: "yes", cls: "bg-err" },
  { id: "talk", cls: "bg-warn" },
  { id: "read", cls: "bg-warn/70" },
  { id: "unread", cls: "bg-warn/40" },
  { id: "no", cls: "bg-fg-faint" },
  { id: "failed", cls: "bg-err/40" },
];

function DayBar({ d, peak }: { d: Pick<WabaDay, "total" | "results">; peak: number }) {
  if (!d.total) return null;
  return (
    <div className="flex h-2.5 overflow-hidden rounded-sm" style={{ width: `${(d.total / peak) * 6}rem` }} aria-hidden>
      {BAR.map((b) => (d.results[b.id] ? <span key={b.id} className={b.cls} style={{ flexGrow: d.results[b.id] }} /> : null))}
    </div>
  );
}

/** Ячейка «По дням», открытая в панели: дни `from`…`to` и колонка. */
type Cell = { from: string; to: string; filter: Filter | "lost" | "came" | "bron"; label: string; title: string };

/** Подпись строки: день «29.09 вт», неделя «22.09 – 28.09». */
function bucketTitle(d: Pick<WabaDay, "from" | "to">): string {
  const dm = (v: string) => `${v.slice(8, 10)}.${v.slice(5, 7)}`;
  return d.from === d.to ? `${dm(d.from)} ${WEEKDAY[new Date(`${d.from}T12:00:00`).getDay()]}` : `${dm(d.from)} – ${dm(d.to)}`;
}

/** Панель справа: номера ячейки «По дням» — те же строки, что во вкладке «Номера». */
function CellPanel({ cell, onClose }: { cell: Cell; onClose: () => void }) {
  const one = cell.from === cell.to;
  const key = ["mailing-waba", "cell", cell.from, cell.to] as const;
  const q = useQuery({ queryKey: key, queryFn: () => loadWaba(24, cell.from, one ? null : cell.to) });
  const client = useQueryClient();
  const onComments = (phone: string, list: WabaComment[]) =>
    client.setQueryData<Waba>(key, (w) => w && { ...w, rows: w.rows.map((r) => (r.phone === phone ? { ...r, comments: list } : r)) });
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const f = cell.filter;
  const rows =
    q.data?.rows.filter((r) =>
      f === "lost" ? r.result !== "booked" : f === "came" ? !!r.came : f === "bron" ? !!r.bron : match(r, f),
    ) ?? [];
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(80rem,100vw)] flex-col border-l border-border bg-bg shadow-xl"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        role="dialog"
        aria-label={`${cell.label} · ${cell.title}`}
        data-annot={`панель ${cell.title} ${cell.label}`}
      >
        <div className="flex flex-wrap items-baseline gap-3 border-b border-border px-4 py-3">
          <h2 className="text-[14px] font-semibold">
            {cell.label} · {cell.title}
          </h2>
          {q.data && <span className="text-[12.5px] tabular-nums text-fg-muted">{rows.length} номеров</span>}
          <button type="button" onClick={onClose} className="ml-auto self-center rounded-[var(--radius-ui)] p-1 hover:bg-bg-hover" title="Закрыть (Esc)">
            <X size={16} aria-hidden />
            <span className="sr-only">Закрыть</span>
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-4">
          {q.isPending ? (
            <div className="text-[12.5px] text-fg-muted">Собираю номера…</div>
          ) : q.isError ? (
            <div className="text-[12.5px] text-err">Не удалось получить номера: {String(q.error.message)}</div>
          ) : (
            <>
              <div className="text-[11.5px] text-fg-faint">
                Лид и сделка — текущее состояние в CRM. Нажмите на строку — под ней вся переписка номера; комментарий пишется к
                номеру и виден всем.
              </div>
              <RowsTable rows={rows} onComments={onComments} />
            </>
          )}
        </div>
      </aside>
    </>
  );
}

const GROUPS: readonly { id: WabaGroup; label: string }[] = [
  { id: "day", label: "по дням" },
  { id: "week", label: "по неделям" },
];

/** «По дням»: воронка отправок, строка — день или неделя; клик по числу — номера в панели справа. */
function DaysView() {
  const { params, patch } = useWabaParams();
  const today = new Date();
  const from = isValidDate(params.get("from") ?? "") ? (params.get("from") as string) : iso(plusDays(today, -13));
  const to = isValidDate(params.get("to") ?? "") ? (params.get("to") as string) : iso(today);
  const group: WabaGroup = params.get("group") === "week" ? "week" : "day";
  const q = useQuery({ queryKey: ["mailing-waba-days", from, to, group], queryFn: () => loadWabaDays(from, to, group) });
  const [cell, setCell] = useState<Cell | null>(null);
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const num = (d: Pick<WabaDay, "from" | "to">, title: string, v: number, tone: string, f: Cell["filter"], label: string, of?: number) => (
    <button
      type="button"
      disabled={!v}
      onClick={() => setCell({ from: d.from, to: d.to, filter: f, label, title })}
      className={`-mx-1.5 rounded-[var(--radius-ui)] px-1.5 py-0.5 text-left ${v ? "cursor-pointer hover:bg-bg-hover hover:ring-1 hover:ring-border-strong" : "cursor-default"}`}
      title={v ? `${label}: номера — ${title}` : undefined}
    >
      <span className={`text-[14px] font-semibold ${v ? tone : "text-fg-faint"}`}>{v}</span>
      {of !== undefined && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, of)}</span>}
    </button>
  );
  const cells = (d: Pick<WabaDay, "from" | "to" | "total" | "results" | "came" | "bron">, title: string, peak: number) => {
    const booked = d.results.booked;
    return (
      <>
        <td className="whitespace-nowrap px-3 py-1 tabular-nums">{num(d, title, d.total, "text-fg", "all", "Отправлено")}</td>
        <td className="whitespace-nowrap px-3 py-1 tabular-nums">{num(d, title, booked, "text-ok", "booked", "Записались", d.total)}</td>
        <td className="whitespace-nowrap px-3 py-1 tabular-nums">{num(d, title, d.came, "text-ok", "came", "Пришли", booked)}</td>
        <td className="whitespace-nowrap px-3 py-1 tabular-nums">{num(d, title, d.bron, "text-ok", "bron", "Забронировали", d.came)}</td>
        <td className="whitespace-nowrap border-r border-border px-3 py-1 tabular-nums">
          {num(d, title, d.total - booked, "text-fg", "lost", "Не записались", d.total)}
        </td>
        {LOST.map((t) => (
          <td key={t.id} className="whitespace-nowrap px-3 py-1 tabular-nums">
            {num(d, title, d.results[t.id], t.tone, t.id, t.label, d.total)}
          </td>
        ))}
        <td className="py-1.5 pl-3 pr-4">
          <DayBar d={d} peak={peak} />
        </td>
      </>
    );
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker
          label="Период"
          from={from}
          to={to}
          onChange={(a, b) => patch({ from: a || null, to: b || null })}
        />
        <Segmented label="Группировка" items={GROUPS} value={group} onChange={(g) => patch({ group: g === "day" ? null : g })} />
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по Wazzup и CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить воронку: {String(q.error.message)}</div>
      ) : (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`воронка WABA ${group === "week" ? "по неделям" : "по дням"}`}>
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`} rowSpan={2}>
                    {group === "week" ? "Неделя" : "День"}
                  </th>
                  <th className={th} rowSpan={2}>
                    Отправлено
                  </th>
                  <th className={th} rowSpan={2} title="после сообщения сделка номера перешла в «Ждем клиента» — на сейчас">
                    Записались
                  </th>
                  <th className={th} rowSpan={2} title="после сообщения клиент пришёл в салон по сделке номера (приход в CRM); % — от записавшихся">
                    Пришли
                  </th>
                  <th className={th} rowSpan={2} title="после сообщения договор сделки перешёл в «Бронь» или «Подписан»; % — от пришедших">
                    Забронировали
                  </th>
                  <th className={`${th} border-r border-border`} rowSpan={2}>
                    Не записались
                  </th>
                  <th className={`${th} text-center`} colSpan={LOST.length}>
                    где потеряли
                  </th>
                  <th className={th} rowSpan={2} />
                </tr>
                <tr>
                  {LOST.map((t) => (
                    <th key={t.id} className="whitespace-nowrap px-3 pb-2 font-normal" title={t.hint}>
                      {t.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const peak = Math.max(1, ...q.data.rows.map((d) => d.total));
                  return q.data.rows.map((d) => {
                    const wd = new Date(`${d.from}T12:00:00`).getDay();
                    const title = bucketTitle(d);
                    return (
                      <tr
                        key={d.from}
                        className={`border-t border-border ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                        data-annot={`${group === "week" ? "неделя" : "день"} ${d.from}`}
                      >
                        <td className={`whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums ${d.total ? "" : "text-fg-faint"}`}>{title}</td>
                        {cells(d, title, peak)}
                      </tr>
                    );
                  });
                })()}
                <tr className="border-t-2 border-border font-medium" data-annot="итого за период">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
                  {cells(q.data, `за ${bucketTitle(q.data)}`, Math.max(1, q.data.total))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            {group === "week" ? "Неделя — с понедельника по дате отправки шаблона, крайние обрезаны периодом" : "День — дата отправки шаблона"};
            сумма «Записались» и «Не записались» = «Отправлено», «где потеряли» раскладывает «Не записались» без пересечений.
            «Записались» — на сейчас: у свежих дней запись ещё дойдёт. Дальше по истории после сообщения: «Пришли» — приход в
            салон по сделке (% от записавшихся), «Забронировали» — договор в «Бронь»/«Подписан» (% от пришедших); сделка номера —
            по лиду или по контакту с этим телефоном. Клик по числу — эти номера справа, с перепиской и
            комментариями. Полоса: зелёный — записались, красный — «Актуально» без записи, жёлтый — пообщались / прочитали / не
            прочитали, серый — «Не актуально», бледно-красный — не дошло.
          </div>
        </section>
      )}
      {cell && <CellPanel key={`${cell.from}-${cell.to}-${cell.filter}`} cell={cell} onClose={() => setCell(null)} />}
    </>
  );
}

const VIEWS = [
  { id: "list", label: "Номера" },
  { id: "days", label: "По дням" },
] as const;

function WabaTab() {
  const { params, patch } = useWabaParams();
  const view = params.get("view") === "days" ? "days" : "list";
  return (
    <section className="flex flex-col gap-3" data-annot="WABA">
      <Segmented label="Вид" items={VIEWS} value={view} onChange={(v) => patch({ view: v === "list" ? null : v })} />
      {view === "days" ? <DaysView /> : <ListView />}
    </section>
  );
}

const TABS = [
  { id: "waba", label: "Wazzup", hint: "шаблоны WhatsApp" },
  { id: "templates", label: "По шаблонам", hint: "воронка: дошло → кабинет → запись → бот" },
  { id: "links", label: "Страница брони", hint: "зашли, боты, push, почта" },
  { id: "fitting", label: "Отзыв о примерке", hint: "канал отправки, «Хорошо» / «Плохо»" },
  { id: "remind", label: "Напоминание о примерке", hint: "накануне записи: канал, подтвердили, отменили, пришли" },
] as const;
type Tab = (typeof TABS)[number]["id"];

export function MailingPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((t) => t.id === params.get("tab")) ? (params.get("tab") as Tab) : "waba";
  const select = (id: Tab) => {
    const out = new URLSearchParams(params);
    if (id === "waba") out.delete("tab");
    else out.set("tab", id);
    setParams(out, { replace: true });
  };
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-annot="шапка рассылок">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Рассылки</h1>
        <div role="tablist" aria-label="Разделы рассылок" className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => select(t.id)}
              className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
                tab === t.id
                  ? "border-accent bg-accent-dim text-accent-text"
                  : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              <span className="text-[15px] font-semibold">{t.label}</span>
              <span className="text-[12px] opacity-80">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>
      {tab === "waba" && <WabaTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "links" && <LinksTab />}
      {tab === "fitting" && <FittingTab />}
      {tab === "remind" && <RemindTab />}
    </main>
  );
}
